/* eslint-disable valid-jsdoc */
/* @flow */

import Video, { RemoteParticipant, Room, Participant } from 'twilio-video';
import { getEventPlugins } from '@braindate/domain/lib/event/util';
import * as Sentry from '@sentry/react';

import {
  getBraindateId,
  getBraindateEndTime,
} from '@braindate/domain/lib/braindate/util';
import { isFishbowlTopic } from '@braindate/domain/lib/topic/util';
import { getConversationParticipants } from '@braindate/domain/lib/conversation/util';
import { getUserId } from '@braindate/domain/lib/user/util';

import { isErrorReportingEnabled } from 'src/shared/app/base/selector/appEnvSelectors';
import {
  videoCallPlugin,
  videoCallPluginProviderTwilio,
} from 'src/shared/app/feature/settings/featureSettings';
import { createDateTimeFromISO } from 'src/shared/app/booking/util/dateUtils';
import {
  notificationInfoLevel,
  notificationTopType,
} from 'src/shared/app/notification/constant/notificationConstants';
import { getBraindateTopic } from 'src/shared/domain/braindate/util/braindateUtil';
import { topicRoute } from 'src/shared/app/base/route/setting/routeSettings';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';
import type { BraindateEvent } from '@braindate/domain/lib/event/type';
import type { Conversation } from '@braindate/domain/lib/conversation/type';
import type { UserSocketParticipant } from 'src/shared/app/base/plugin/virtual-braindate/type/virtualTypes';
import { isSpeaker } from './virtualBraindateFishbowlUtils';
import { FISHBOWL_STATUS } from '../constant/virtualBraindateConstants';
import type { User } from '@braindate/domain/lib/user/type';

export function silentError(e: Error) {
  if (isErrorReportingEnabled) {
    Sentry.captureException(e);
  }
  // eslint-disable-next-line no-console
  console.error(e);
}

export function getParticipantsBindToUsersSocketParticipants(
  context: Object,
  conversation?: Conversation,
): UserSocketParticipant[] {
  const participants =
    conversation && getConversationParticipants(conversation);
  if (!context.participants || !participants) {
    return [];
  }
  return context.participants
    .map((APIp) => {
      const conversationParticipant = participants.find(
        (p) => APIp.user === getUserId(p),
      );
      if (!conversationParticipant) return false;
      return {
        ...conversationParticipant,
        fishbowl_data: APIp,
      };
    })
    .filter(Boolean);
}

export function buildConversationParticipants(
  isFishbowl: boolean,
  participants: RemoteParticipant[],
  conversationSocketParticipants: UserSocketParticipant[],
  localStatus?: string,
  room?: Room,
): RemoteParticipant[] {
  if (!isFishbowl) return participants;

  const fishbowlParticipants = participants.filter((participant) => {
    const userSocketParticipant = conversationSocketParticipants.find(
      (user) => getUserId(user) === Number(participant.identity),
    );

    return userSocketParticipant && isSpeaker(userSocketParticipant);
  });

  if (localStatus !== FISHBOWL_STATUS.WATCHER && room?.localParticipant) {
    fishbowlParticipants.push(room?.localParticipant);
  }

  return fishbowlParticipants;
}

export function buildVirtualBraindateEarlyReminder(
  braindate: Braindate,
): NotificationType<> {
  const topic = getBraindateTopic(braindate);
  const isFishbowl = isFishbowlTopic(topic);

  return {
    id: `virtual-braindate-early-reminder-${getBraindateId(braindate)}`,
    level: notificationInfoLevel,
    type: notificationTopType,
    componentClass: 'VirtualBraindateEarlyReminder',
    data: braindate,
    exclude: isFishbowl ? [topicRoute] : ['braindateRoute'],
  };
}

export function getSecondsBeforeBraindateEndTime(braindate: Braindate): number {
  // $FlowIssue
  const endTime = createDateTimeFromISO(getBraindateEndTime(braindate));
  const { milliseconds } = endTime.diffNow();
  const seconds = milliseconds / 1000;

  return Math.round(seconds);
}

export function isProvider(event: BraindateEvent, provider: string) {
  if (!provider) {
    return false;
  }
  const plugin = getEventPlugins(event)[videoCallPlugin];
  return plugin && plugin.provider === provider;
}

export function isSupported(event: BraindateEvent) {
  if (isProvider(event, videoCallPluginProviderTwilio)) {
    return Video.isSupported;
  }
  return true;
}

export function getConversationUserFromTwilioParticipant(
  conversation: ?Conversation,
  participant: ?Participant,
): ?User {
  if (!participant) return null;
  const conversationParticipants =
    conversation && getConversationParticipants(conversation);
  const conversationParticipant =
    conversationParticipants &&
    conversationParticipants.find(
      (current) => current.id === Number(participant.identity),
    );
  return conversationParticipant;
}
